import React, { useState, useEffect } from "react";
import Input from "../../components/form/Input";
import Tabs from "../../components/elements/Tabs";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { postMenu } from "../../services/postService";
import { taxonomyList } from "../../services/taxonomyService";
import Image from "../../components/elements/Image";
import { useDebounce } from "../../hooks/useDebounce";
import siteLogo from "../../assets/images/logo.png";
import { Link, useParams } from "react-router-dom";
import { 
  MenuItemSkeleton, 
  CategoryTabSkeleton, 
  EmptyMenuState 
} from "../components/loaders/MenuSkeletons";
import { apiRequest } from "../../util/apiUtils";

const Menu = () => {
  const params = useParams();
  const tableNumber = params?.table;
  const [selectedIndex, setSelectedIndex] = useState(0); 
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loadingCategories,setLoadingCategories] = useState(false);
  const [loadingMenu,setLoadingMenu] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const result = await apiRequest("get", `taxonomy/list`, {
          queries: {
            type: "product-category",
          },
        });
        if (result.status === 200) {
          setCategories([
            { _id: "all", label: "All", description: "Show all items" }, 
            ...result?.data?.docs?.data?.map((cat) => ({
              _id: cat?._id,
              label: cat?.content['en'].title,
              description: cat?.content['en'].content || "",
            }))
          ]);
        }
        setLoadingCategories(false); 
      } catch (err) {
        console.log(err.message);
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  const selectedCategoryId = categories[selectedIndex]?._id;

  useEffect(() => {
    const loadMenus = async () => {
      setLoadingMenu(true);
      try {
        const params = {}; 
        if (search && search.trim()) {
          params.keyword = search.trim();
        }
        if (selectedCategoryId && selectedCategoryId !== "all") {
          params.categories = [selectedCategoryId];
        } 
        const result = await apiRequest("get", `post/menu`, {
          queries: params,
        });
        if (result.status === 200) {
          setMenuItems(result?.data?.docs?.data);
        }
        setLoadingMenu(false); 
      } catch (err) {
        console.log(err.message);
        setLoadingMenu(false);
      }
    };
    loadMenus();
  }, [search,selectedCategoryId]);
 

  return (
    <div className="w-full xls:max-w-sm bg-white rounded-2xl shadow-sm overflow-hidden pb-0 h-screen xls:mx-auto flex flex-col">
      {/* Header */}
      <div className="border-b border-[#F4F4F4] flex-shrink-0">
        <div className="flex items-center justify-between gap-4 px-4 py-5">
          <Link to={(tableNumber && tableNumber !== undefined)?`/bistro/home/table/${tableNumber}`:`/bistro/home`} className="w-10 aspect-square overflow-hidden flex-shrink-0">
            <Image src={siteLogo} classes={"object-cover"} />
          </Link>
          <div className="relative w-full">
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              inputPlaceholder="Search menu items..."
              inputClasses="w-full h-11 rounded-xl !border !border-green-200 px-5 pr-12 text-gray-700 placeholder-gray-400 !shadow-sm focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-transparent"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center text-green-900">
              <div className="h-5 border-l border-green-200 mr-3"></div>
              <i className="fa-solid fa-magnifying-glass text-green-900 text-sm"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs (Category Selection) */}
      <div className="py-6 pl-4 flex-shrink-0">
        {loadingCategories ? (
          <CategoryTabSkeleton />
        ) : (
          <Tabs
            items={categories?.map((cat, i) => ({
              label: (
                <span
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer ${
                    selectedIndex === i
                      ? "bg-[#F0FAF0] text-[#224722] border border-green-700 shadow-sm"
                      : "text-gray-700 hover:bg-gray-50 border border-transparent"
                  }`}
                >
                  {cat.label}
                </span>
              ),
            }))}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            content={false}
            tabsClass="flex gap-2 py-2 overflow-x-auto no-scrollbar items-center xls:custom-scroll"
            type="tabs"
          />
        )}
      </div>

      {/* Menu List */}
      <div className="px-4 space-y-3 py-4 overflow-auto flex-1">
        {loadingMenu ? (
          // Skeleton Loader
          <>
            {[1, 2, 3, 4, 5].map((i) => (
              <MenuItemSkeleton key={i} />
            ))}
          </>
        )  : menuItems.length === 0 ? (
          // Empty State
          <EmptyMenuState 
            search={search} 
            //onClearSearch={search ? handleClearSearch : null}
          />
        ) : (
          // Menu Items
          menuItems.map((item, idx) => (
            <Link
              to={(tableNumber && tableNumber !== undefined)?`/bistro/menu-details/${item?.slug || item?._id}/${tableNumber}`:`/bistro/menu-details/${item?.slug || item?._id}`}
              key={item?._id || idx}
              className="flex items-center bg-white rounded-xl shadow-sm border border-green-200 p-3 hover:border-green-400 hover:shadow-md transition-all duration-200 cursor-pointer group"
            >
              <div className="flex-1 pr-3">
                <h3 className="font-semibold text-[#232222] text-base mb-1 line-clamp-1 group-hover:text-green-700 transition-colors">
                  {item?.content?.en?.title || "Untitled Item"}
                </h3>
                <p className="text-[#9FA1A3] text-sm font-normal line-clamp-2">
                  {item?.content?.en?.content || item?.description || "No description available"}
                </p>
                {item?.price && (
                  <p className="text-green-700 font-bold text-base mt-2">
                    ${parseFloat(item.price).toFixed(2)}
                  </p>
                )}
              </div>
              <div className="w-24 aspect-square rounded-2xl overflow-hidden flex-shrink-0 bg-gray-100">
                {item?.coverMedia?.url ? (
                  <Image 
                    src={item.coverMedia.url} 
                    alt={item?.coverMedia?.name || item?.title} 
                    classes="object-cover w-full h-full group-hover:scale-105 transition-transform duration-200" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                    <i className="fa-solid fa-utensils text-gray-300 text-2xl"></i>
                  </div>
                )}
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default Menu;